// HAND-AUTHORED FOR NOW — slot follows the v2gen override-block layout
// so a future generator run can adopt it without re-shaping. There is
// no kiosk.entity-hint.yaml today (the canary entity-hints were
// authored CHG-1228 / CHG-1234 / CHG-1235 / CHG-1237 / CHG-1249 /
// CHG-1255 / CHG-1328; kiosk-management hasn't been canaried yet).
// When that hint lands, the helpers + column definitions below move
// into override blocks marked `// override-block:<id> BEGIN / END`
// and the surrounding shell becomes generator output.
//
// Source of truth (today): this file + kiosk-list.cells.tsx (cell JSX).
// Future source of truth: specs/tier-05-experience/kiosk.entity-hint.yaml.
// Generator: tools/v2-gen/v2gen.py (post-hint).
// Screen spec:  specs/tier-05-experience/kiosk-management-list.cross-screen.yaml.
//
// CHG-2061 / ITER-4159 (kiosk-γ.1) — dashboard-v2 port of v1
// web/dashboard/src/pages/Kiosks.tsx (CHG-1198 added Type column).

import type { ListPageConfig } from "../../components/composition/ListPage";
import { kiosksApi } from "../../api/kiosks";
import type { Kiosk, KioskStatus } from "../../api/types";
// JSX cell renderers live in a sibling `.tsx` so this config can stay
// `.ts` like every other v2gen screen config.
import {
  readerTypeBadge,
  onlineDot,
  batteryCell,
  appVersionCell,
} from "./kiosk-list.cells";

// override-block:helpers BEGIN

const STATUS_LABEL: Record<KioskStatus, string> = {
  draft: "Draft",
  active: "Active",
  maintenance: "Maintenance",
  disabled: "Disabled",
};

function statusLabelCell(row: Kiosk): string {
  return STATUS_LABEL[row.status] ?? row.status;
}

// override-block:helpers END

export const kioskListConfig: ListPageConfig<Kiosk> = {
  title: "Kiosks",
  subtitle:
    "In-person tablet kiosks with Stripe Terminal — monitor health, manage pairing, and surface readers that need attention.",
  queryKey: ["kiosks", "list"] as const,
  queryFn: ({ page, pageSize, sortField, sortDirection, search, filters }) =>
    kiosksApi.listKiosks({
      page,
      pageSize,
      sortField,
      sortDirection,
      search,
      filters,
    }),
  defaultSort: { field: "name", direction: "asc" },
  pageSize: 50,
  rowHref: "/kiosks/{id}",
  search: { enabled: true, placeholder: "Search kiosks by name…" },
  emptyState: {
    title: "No kiosks registered",
    subtitle:
      "Kiosks are tablet devices for in-person donation collection with Stripe Terminal card readers.",
  },
  filters: [
    {
      field: "status",
      label: "Status",
      type: "select",
      options: [
        { value: "active", label: "Active" },
        { value: "draft", label: "Draft" },
        { value: "maintenance", label: "Maintenance" },
        { value: "disabled", label: "Disabled" },
      ],
    },
    {
      // CHG-1198 — type filter chip lets admins isolate Tap-to-Pay vs
      // M2 fleets when troubleshooting reader-class-specific issues.
      // BE filter is forward-promised: GET /kiosks accepts only
      // page/per_page/search/status today (tier-04 spec lines 307-311),
      // so toggling this chip is a no-op until the BE wires
      // reader_type → KioskService.list() — tracked in DRIFT-823.
      // FastAPI drops unrecognized query params silently; same pattern
      // as donations.ts forward-promised date_range (DRIFT-714).
      field: "reader_type",
      label: "Type",
      type: "select",
      options: [
        { value: "m2", label: "M2 (Hardware)" },
        { value: "tap_to_pay", label: "Tap-to-Pay (Soft)" },
      ],
    },
  ],
  columns: [
    { field: "name", label: "Name", type: "text", sortable: true },
    { field: "location", label: "Location", type: "text" },
    {
      field: "last_seen_at",
      label: "Online",
      type: "custom",
      render: onlineDot,
    },
    {
      field: "latest_heartbeat",
      label: "App version",
      type: "custom",
      render: appVersionCell,
    },
    {
      // CHG-1198 / ITER-3317 — Type column. Renders Tap-to-Pay (Soft)
      // vs M2 (Hardware) badge driven off row.reader_type.
      field: "reader_type",
      label: "Type",
      type: "custom",
      render: readerTypeBadge,
    },
    { field: "reader_status", label: "Reader", type: "text" },
    {
      field: "latest_heartbeat",
      label: "Battery",
      type: "custom",
      render: batteryCell,
    },
    {
      field: "status",
      label: "Status",
      type: "custom",
      render: statusLabelCell,
    },
  ],
};
